import { Maybe } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';

type handleClickedSuggestionAndGetElementsProps = {
  scrollTo: Maybe<HTMLElement>;
  mustScrollUntil: Maybe<HTMLElement>;
};

export const handleClickedSuggestionAndGetElements = async (
  clickedSuggestion: Maybe<GenericSuggestion>
): Promise<handleClickedSuggestionAndGetElementsProps> => {
  if (!clickedSuggestion) {
    return {
      scrollTo: null,
      mustScrollUntil: null,
    };
  }

  let scrollTo: Maybe<HTMLElement> = null; // lowest vertical offset of all inputs
  let mustScrollUntil: Maybe<HTMLElement> = null; // highest vertical offset of all inputs

  // since we'll be dealing with element position, sleep wait for js to run a couple of event cycles and properly load DOM
  await new Promise((resolve) => setTimeout(resolve, 200));

  // Helper function to build field names for nested properties
  const findAndScrollToElements = (prefix: string, obj: any) => {
    Object.entries(obj).forEach(([key, value]) => {
      const fullFieldName = prefix ? `${prefix}.${key}` : key;

      // Map suggestion keys onto form field names (pipelines don't always match RHF paths).
      let fieldNames: string[] = [fullFieldName];
      if (clickedSuggestion.pipeline === SuggestionPipelines.CarrierInfo) {
        fieldNames = [`carrier.${fullFieldName}`];
      } else if (
        clickedSuggestion.pipeline === SuggestionPipelines.ApptConfirmation
      ) {
        if (key === 'pickupApptTime') {
          fieldNames = ['pickup.apptStartTime', 'stops.0.apptStartTime'];
        } else if (key === 'dropoffApptTime') {
          fieldNames = ['consignee.apptStartTime'];
        }
      }

      for (const fieldName of fieldNames) {
        const fieldElem = document.querySelector(
          `[name="${fieldName}"], [data-name="${fieldName}"]`
        ) as HTMLElement;

        if (fieldElem?.offsetTop == null) {
          continue;
        }

        const fieldOffsetTop = fieldElem.offsetTop;
        if (!scrollTo || fieldOffsetTop <= scrollTo.offsetTop) {
          scrollTo = fieldElem;
        }
        if (!mustScrollUntil || fieldOffsetTop >= mustScrollUntil.offsetTop) {
          mustScrollUntil = fieldElem;
        }
      }

      // Recursively check nested fields if `value` is an object (for example, for `customer.addressLine1` in load building)
      if (value && typeof value === 'object') {
        findAndScrollToElements(fullFieldName, value);
      }
    });
  };

  // Start with the root object, `clickedSuggestion.suggested`
  findAndScrollToElements('', clickedSuggestion.suggested);

  return { scrollTo, mustScrollUntil };
};
